/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnDestroy, Directive, Injector, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutComponent, TabComponent } from '@dm/core';
import { type Subscription } from 'rxjs';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class RouterActionAbstractComponent implements AfterViewInit, OnDestroy {
    router: Router;
    route: ActivatedRoute;
    layout: LayoutComponent;
    tabs: TabComponent;
    tabName: string;

    private subs$: Subscription[] = [];

    constructor(@Optional() protected injector: Injector) {
        this.router = injector.get(Router, null);
        this.route = injector.get(ActivatedRoute, null);
        this.layout = injector.get(LayoutComponent, null);
    }

    ngAfterViewInit(): void {
        this.subs$.push(
            this.route.queryParams.subscribe(() => {
                this.routeQueryParamsChange();
            }),
        );
    }

    ngOnDestroy(): void {
        for (const sub$ of this.subs$) {
            sub$.unsubscribe();
        }
    }

    routeQueryParamsChange(): void {
        // 支持租户的页面需要等待租户加载完毕后再打开弹出框
        if (this.layout && this.layout.activeItem && this.layout.activeItem.vstore) {
            if (this.layout.vstoreInited) {
                this.doAction();
            } else {
                let sub$ = this.layout.vstoreChange.subscribe(() => {
                    this.doAction();
                    sub$.unsubscribe();
                    sub$ = null;
                });
            }
        } else {
            this.doAction();
        }
    }

    doAction(): void {
        const tabs = this.injector.get(TabComponent, null);
        const action = this.route.snapshot.queryParams.action;
        const actionParams = this.route.snapshot.queryParams.actionParams;
        const actionTab = this.route.snapshot.queryParams.subTab ?? this.route.snapshot.queryParams.tab;

        if ((!tabs || this.tabName === actionTab) && action && this[action]) {
            this[action](actionParams);
        }
    }

    clearActionParams(): void {
        const action = this.route.snapshot.queryParams.action;
        if (action && this[action]) {
            this.router.navigate([], {
                queryParams: null,
            });
        }
    }
}
